import { jsx as _jsx } from "react/jsx-runtime";
import './initSentry'; // this must be before any other imports
import { Router } from 'react-router-dom';
import DateFnsUtils from '@date-io/date-fns';
import { GTMProvider } from '@elgorditosalsero/react-gtm-hook';
import * as loom from '@loomhq/loom-embed';
import { createBrowserHistory } from 'history';
import isMobileDevice from 'is-mobile';
import Lottie from 'lottie-react';
import Persona from 'persona';
import Pusher from 'pusher-js';
import { createRoot } from 'react-dom/client';
import { usePlaidLink } from 'react-plaid-link';
import ReactPlayer from 'react-player';
import YouTubePlayer from 'react-player/youtube';
import { GoogleButton, MuiPickersUtilsProvider, onlyShowFocusOnTabs } from 'seagull';
import { IoContextProvider } from '~/contexts';
import { PusherProvider } from '~/pusher';
import { slackClient } from '~/utils/slack-client';
import { AppWithEventReporter } from './App';
import { uploadFileToCloudStorage } from './infra/utils';
import { RealIntercomClient } from './intercom/intercomClient';
import { RealCookiesManager } from './providers/CookiesManager';
import { RootDeviceProvider } from './providers/DeviceProvider';
import { MobileViewportWidthProvider } from './providers/MobileViewportWidthProvider';
import { initStoreApi } from './store/store-api';
import { StoreProvider } from './store/StoreProvider';
import { actualReactStripeJs } from './stripe/reactStripeJs';
import { actualDownloadCsv } from './utils/csv.utils';
import { getTimezoneAbbreviation } from './utils/date-timezone.utils';
import { initializeLogBIEvent, sendBiViaBeacon } from './utils/logBIEvent/logBIEvent';
import { logBIEventOld } from './utils/logBIEventOld';
onlyShowFocusOnTabs();
const history = createBrowserHistory();
const gtmParams = {
    id: window._gtmId
};
const getPusher = ()=>{
    const pusher = new Pusher(window.pusherAppKey, {
        cluster: 'us2'
    });
    return {
        subscribe: (channelName)=>{
            return pusher.subscribe(channelName);
        },
        unsubscribe: (channelName)=>{
            return pusher.unsubscribe(channelName);
        },
        channel: (channelName)=>{
            return pusher.channel(channelName);
        }
    };
};
const getHotjar = ()=>({
        identify (userId, payload) {
            window.hj && window.hj('identify', userId, payload);
        }
    });
const loomParser = (url)=>loom.oembed(url);
const makePersonaClient = (param)=>{
    let { onError, onReady, onComplete, onCancel } = param;
    return {
        open (inquiryId, sessionToken) {
            const client = new Persona.Client({
                inquiryId,
                sessionToken: sessionToken || undefined,
                onError,
                onReady: ()=>{
                    onReady && onReady();
                    client.open();
                },
                onComplete,
                onCancel
            });
        }
    };
};
function injectScriptOrLink(script) {
    return new Promise((resolve, reject)=>{
        if (script.id) {
            const existingScript = document.getElementById(script.id);
            if (existingScript) {
                return resolve(null);
            }
        }
        script.onload = ()=>resolve(null);
        script.onerror = ()=>reject();
        document.head.appendChild(script);
    });
}
const root = createRoot(document.getElementById('main'));
root.render(/*#__PURE__*/ _jsx(Router, {
    history: history,
    children: /*#__PURE__*/ _jsx(IoContextProvider, {
        getNow: ()=>new Date(),
        usePlaidLink: usePlaidLink,
        slackClient: slackClient,
        locationReload: ()=>window.location.reload(),
        changeLocation: (url)=>window.location.href = url,
        copyToClipboard: (text)=>navigator.clipboard.writeText(text),
        intercomClient: new RealIntercomClient(),
        getHotjar: getHotjar,
        openNewTab: function openNewTab() {
            for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
                args[_key] = arguments[_key];
            }
            return window.open(...args);
        },
        personaClient: makePersonaClient,
        ReactPlayer: ReactPlayer,
        YoutubePlayer: YouTubePlayer,
        Lottie: Lottie,
        GoogleButton: GoogleButton,
        loomParser: loomParser,
        uploadFileToCloudStorage: uploadFileToCloudStorage,
        cookies: new RealCookiesManager(window._authDomain, window.localStorage),
        logBIEventOld: logBIEventOld,
        logBIEvent: initializeLogBIEvent(sendBiViaBeacon),
        localStorage: window.localStorage,
        downloadCsv: actualDownloadCsv,
        reactStripe: actualReactStripeJs,
        getTimezoneAbbreviation: getTimezoneAbbreviation,
        injectScriptOrLink: injectScriptOrLink,
        children: /*#__PURE__*/ _jsx(StoreProvider, {
            apiHost: window._apiHostname,
            mgmtHost: window._mgmtHostname,
            pdfHost: window._pdfHostname,
            apiGatewayHost: window._apiGatewayHostname,
            authDomain: window._authDomain,
            enableAppcues: window.enableAppcues,
            appcuesAccountId: window.appcuesAccountId,
            useAuthorizationHeader: window.useAuthorizationHeader,
            sessionTimeoutMS: window.sessionTimeoutMS,
            initStoreApi: initStoreApi,
            children: /*#__PURE__*/ _jsx(RootDeviceProvider, {
                isMobileDevice: isMobileDevice(),
                children: /*#__PURE__*/ _jsx(MuiPickersUtilsProvider, {
                    utils: DateFnsUtils,
                    children: /*#__PURE__*/ _jsx(GTMProvider, {
                        state: gtmParams,
                        children: /*#__PURE__*/ _jsx(PusherProvider, {
                            pusher: getPusher(),
                            children: /*#__PURE__*/ _jsx(MobileViewportWidthProvider, {
                                children: /*#__PURE__*/ _jsx(AppWithEventReporter, {})
                            })
                        })
                    })
                })
            })
        })
    })
}));
